import React, { useMemo } from "react";
import { inject, observer } from "mobx-react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import PricingStore from "../_stores/pricing/PricingStore";
import LanguageStore from "library/core/stores/language/LanguageStore";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindButton from "library/components/_tailwind/button";
import TailwindText from "library/components/_tailwind/text";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

interface Props {
  broadcastStore?: BroadcastStore;
  pricingStore?: PricingStore;
  languageStore?: LanguageStore;
}

const MobileBroadcastPrivateRequest: React.FC<
  Props & WrappedComponentProps
> = ({ broadcastStore, pricingStore, languageStore }) => {
  const {
    isPrivateConsentOpen,
    currentPrivateRequest,
    privateShowRequestAccept,
    privateShowRequestAskForLater,
    privateShowRequestDeclineForSession,
    privateChatPrice,
    partyChatPrice,
    currentPrivateRequestRemainingSeconds: remainingSeconds,
  } = broadcastStore!;
  const { modelProducts } = pricingStore!;
  const { intl } = languageStore!;

  const showLabel = useMemo(() => {
    return currentPrivateRequest?.show_type === "VERIFIER"
      ? "Verifier"
      : currentPrivateRequest?.show_type === "GROUP"
      ? "Party"
      : "Private";
  }, [currentPrivateRequest]);

  const price = useMemo(() => {
    if (currentPrivateRequest?.show_type === "GROUP") {
      return partyChatPrice ? partyChatPrice : modelProducts?.party_chat;
    } else {
      return privateChatPrice ? privateChatPrice : modelProducts?.private_chat;
    }
  }, [currentPrivateRequest, modelProducts]);

  if (!isPrivateConsentOpen) {
    return null;
  }

  return (
    <TailwindFlex
      position="fixed"
      inset={["top-0", "left-0"]}
      width="w-screen"
      alignItems="items-center"
      justifyContent="justify-center"
      padding={["px-6"]}
      style={{
        height: "100dvh",
        zIndex: 100,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}>
      <TailwindFlex
        width="w-full"
        maxWidth="max-w-sm"
        flexDirection="flex-col"
        alignItems="items-center"
        justifyContent="justify-between"
        padding={["px-4", "py-5"]}
        borderRadius="rounded-xl"
        style={{ backgroundColor: "#f5d742" }}>
        <TailwindText
          fontSize="text-lg"
          fontWeight="font-bold"
          padding={["pb-3"]}>
          {intl.formatMessage(
            {
              id: "free-to-enter-private.request.title",
              defaultMessage: "{showLabel} Show Request",
            },
            {
              showLabel: intl.formatMessage({
                id: `showTypes2.${showLabel.toLowerCase()}`,
                defaultMessage: showLabel,
              }),
            }
          )}
        </TailwindText>
        {showLabel === "Verifier" ? (
          <TailwindText fontSize="text-sm" textAlign="text-center">
            {intl.formatMessage(
              {
                id: "free-to-enter-private.request.member-verify",
                defaultMessage: "{ member } wants to take you to verify",
              },
              { member: currentPrivateRequest?.member_username }
            )}
          </TailwindText>
        ) : (
          <>
            <TailwindText fontSize="text-sm" textAlign="text-center">
              {intl.formatMessage(
                {
                  id: "free-to-enter-private.request.member",
                  defaultMessage:
                    "{member} wants a {showLabel} Show with you!",
                },
                {
                  member: currentPrivateRequest?.member_username,
                  showLabel: intl.formatMessage({
                    id: `showTypes2.${showLabel.toLowerCase()}`,
                    defaultMessage: showLabel,
                  }),
                }
              )}
            </TailwindText>
            <TailwindText
              fontSize="text-sm"
              padding={["pt-2"]}
              textAlign="text-center">
              {intl.formatMessage(
                {
                  id: "free-to-enter-private.request.price",
                  defaultMessage: "{ price } tokens / minute",
                },
                { price }
              )}
            </TailwindText>
          </>
        )}
        <TailwindFlex
          justifyContent="justify-center"
          width="w-full"
          padding={["py-4"]}>
          <TailwindButton
            justifyContent="justify-center"
            rounded={false}
            borderWidth={["border-2"]}
            borderColor="border-black"
            backgroundColor="bg-green-500"
            textColor="text-white"
            size="normal"
            fullWidth={false}
            padding={["px-8", "py-2"]}
            fontSize="text-lg"
            fontWeight="font-bold"
            onClick={privateShowRequestAccept}>
            {intl.formatMessage({
              id: "free-to-enter-private.request.button",
              defaultMessage: "Yes!",
            })}
          </TailwindButton>
        </TailwindFlex>
        <TailwindFlex
          width="w-full"
          justifyContent="justify-around"
          padding={["pb-4"]}>
          <TailwindText
            fontSize="text-sm"
            cursor="cursor-pointer"
            style={{ textDecoration: "underline" }}
            onClick={privateShowRequestAskForLater}>
            {intl.formatMessage({
              id: "free-to-enter-private.ask",
              defaultMessage: "Ask later",
            })}
          </TailwindText>
          <TailwindText
            fontSize="text-sm"
            cursor="cursor-pointer"
            style={{ textDecoration: "underline" }}
            onClick={privateShowRequestDeclineForSession}>
            {intl.formatMessage({
              id: "free-to-enter-private.decline",
              defaultMessage: "Decline",
            })}
          </TailwindText>
        </TailwindFlex>
        <TailwindFlex alignItems="items-center" gap="gap-2">
          <AccessTimeIcon style={{ fontSize: 18 }} />
          <TailwindText fontSize="text-xs">
            {intl.formatMessage(
              {
                id: "free-to-enter-private.request.footer",
                defaultMessage: "Please respond within {second} seconds",
              },
              { second: remainingSeconds }
            )}
          </TailwindText>
        </TailwindFlex>
      </TailwindFlex>
    </TailwindFlex>
  );
};

export default injectIntl(
  inject(
    "broadcastStore",
    "pricingStore",
    "languageStore"
  )(observer(MobileBroadcastPrivateRequest))
);
